import React from 'react';

/**
 * CreateCategoryModal - Modal for creating new categories
 * Now includes 3-level privacy: Private, Friends Only, Public
 */
const CreateCategoryModal = ({
  show,
  onClose,
  newCategory,
  setNewCategory,
  categories,
  onCreate
}) => {
  if (!show) return null;

  // Privacy options
  const privacyOptions = [
    { value: 'private', label: 'Private (Only Me)', icon: '🔒', color: '#ef4444', desc: 'Only you can see this category' },
    { value: 'friends', label: 'Friends Only', icon: '👥', color: '#f59e0b', desc: 'You and your friends can see this' },
    { value: 'public', label: 'Public (Everyone)', icon: '🌍', color: '#10b981', desc: 'Visible to all users worldwide' }
  ];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Create Category</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
          <input
            className="input-field"
            placeholder="Category Name"
            value={newCategory.name}
            onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
            data-testid="category-name-input"
          />
          <textarea
            className="input-field"
            placeholder="Protocol (e.g., (word1 or word2) & (word3)+ )"
            rows={4}
            value={newCategory.protocol}
            onChange={(e) => setNewCategory({ ...newCategory, protocol: e.target.value })}
            style={{ resize: 'vertical' }}
            data-testid="category-protocol-input"
          />
          <select
            className="input-field"
            value={newCategory.parent_id || ''}
            onChange={(e) => setNewCategory({ ...newCategory, parent_id: e.target.value || null })}
          >
            <option value="">No Parent (Top Level)</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>
          
          {/* Privacy Controls - 3 Radio Buttons */}
          <div style={{
            background: 'linear-gradient(135deg, rgba(252, 211, 77, 0.1), rgba(253, 230, 138, 0.1))',
            borderRadius: 12,
            padding: 15,
            border: '1px solid rgba(252, 211, 77, 0.3)'
          }}>
            <p style={{ color: '#047857', fontWeight: 600, marginBottom: 12, fontSize: '0.95rem' }}>
              🔐 Category Privacy
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {privacyOptions.map(option => (
                <label
                  key={option.value}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '12px 15px',
                    borderRadius: 10,
                    cursor: 'pointer',
                    background: (newCategory.privacy || 'private') === option.value 
                      ? `${option.color}20` 
                      : '#475569',
                    border: (newCategory.privacy || 'private') === option.value 
                      ? `2px solid ${option.color}` 
                      : '1px solid rgba(255,255,255,0.1)',
                    transition: 'all 0.2s'
                  }}
                  data-testid={`privacy-${option.value}`}
                >
                  <input
                    type="radio"
                    name="privacy"
                    value={option.value}
                    checked={(newCategory.privacy || 'private') === option.value}
                    onChange={(e) => setNewCategory({ 
                      ...newCategory, 
                      privacy: e.target.value,
                      is_public: e.target.value === 'public'
                    })}
                    style={{ 
                      accentColor: option.color,
                      width: 18,
                      height: 18
                    }}
                  />
                  <span style={{ fontSize: '1.3rem' }}>{option.icon}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ 
                      color: (newCategory.privacy || 'private') === option.value ? '#fff' : '#a1a1aa',
                      fontWeight: 600,
                      fontSize: '0.9rem'
                    }}>
                      {option.label}
                    </div>
                    <div style={{ 
                      color: '#71717a', 
                      fontSize: '0.75rem',
                      marginTop: 2
                    }}>
                      {option.desc}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </div>
          
          {/* Protocol Syntax Help */}
          <div style={{
            background: 'rgba(16, 185, 129, 0.1)',
            border: '1px solid rgba(16, 185, 129, 0.3)',
            borderRadius: 8,
            padding: 12,
            fontSize: '0.8rem'
          }}>
            <p style={{ color: '#10b981', fontWeight: 600, marginBottom: 8 }}>📝 Protocol Syntax Help:</p>
            <ul style={{ color: '#a1a1aa', margin: 0, paddingLeft: 20, lineHeight: 1.6 }}>
              <li><code style={{ color: '#047857' }}>or</code> - Separate alternatives within a group</li>
              <li><code style={{ color: '#047857' }}>&</code> <strong>or</strong> <code style={{ color: '#047857' }}>and</code> - Connect groups (both work!)</li>
              <li><code style={{ color: '#047857' }}>+</code> - Boost priority of a group</li>
              <li><code style={{ color: '#047857' }}>-</code> - Exclude matches containing these terms</li>
            </ul>
            <p style={{ color: '#22d3ee', marginTop: 10, marginBottom: 0 }}>
              ✨ <strong>Tip:</strong> "and" works the same as "&" between groups!<br/>
              <span style={{ color: '#a1a1aa' }}>Example: <code>(aviation or pilot) and (safety)</code></span>
            </p>
          </div>
          
          <button 
            type="button"
            className="btn btn-primary" 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              console.log('Create Category button clicked');
              if (onCreate) {
                onCreate();
              } else {
                console.error('onCreate handler is not defined');
              }
            }} 
            data-testid="create-category-btn"
          >
            Create Category
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * EditCategoryModal - Modal for editing existing categories
 * Now includes 3-level privacy: Private, Friends Only, Public
 */
const EditCategoryModal = ({
  editingCategory,
  onClose,
  editCategoryName,
  setEditCategoryName,
  editProtocol,
  setEditProtocol,
  editPrivacy,
  setEditPrivacy,
  user,
  onSave,
  onDelete,
  onCleanCategory,
  onCopyProtocol
}) => {
  // Track if click started on the overlay (not modal content)
  const [clickStartedOnOverlay, setClickStartedOnOverlay] = React.useState(false);
  const [isTextSelecting, setIsTextSelecting] = React.useState(false);
  
  if (!editingCategory) return null;

  // Privacy options
  const privacyOptions = [
    { value: 'private', label: 'Private (Only Me)', icon: '🔒', color: '#ef4444', desc: 'Only you can see this category' },
    { value: 'friends', label: 'Friends Only', icon: '👥', color: '#f59e0b', desc: 'Friends can see and copy this protocol' },
    { value: 'public', label: 'Public (Everyone)', icon: '🌍', color: '#10b981', desc: 'Visible to all users worldwide' }
  ];

  // Track mouse down position to detect text selection
  const handleOverlayMouseDown = (e) => {
    // Only track if mousedown is on the overlay itself (not modal content)
    if (e.target === e.currentTarget) {
      setClickStartedOnOverlay(true);
    } else {
      setClickStartedOnOverlay(false);
    }
    setIsTextSelecting(false);
  };

  // Detect if user is selecting text (drag movement)
  const handleOverlayMouseMove = (e) => {
    if (e.buttons === 1) { // Left mouse button is held
      setIsTextSelecting(true);
    }
  };

  // Only close modal if:
  // 1. Click started on overlay (not modal content)
  // 2. Click ended on overlay
  // 3. User was NOT selecting text
  const handleOverlayMouseUp = (e) => {
    // If user was selecting text, don't close
    if (isTextSelecting || window.getSelection()?.toString()?.length > 0) {
      setClickStartedOnOverlay(false);
      setIsTextSelecting(false);
      return;
    }
    
    // Only close if both mousedown and mouseup were on the overlay
    if (e.target === e.currentTarget && clickStartedOnOverlay) {
      onClose();
    }
    setClickStartedOnOverlay(false);
    setIsTextSelecting(false);
  };

  // Prevent any click propagation from modal content
  const handleModalClick = (e) => {
    e.stopPropagation();
  };

  // Stop all events from modal content reaching overlay
  const handleModalMouseDown = (e) => {
    e.stopPropagation();
    setClickStartedOnOverlay(false);
  };

  return (
    <div 
      className="modal-overlay" 
      onMouseDown={handleOverlayMouseDown}
      onMouseMove={handleOverlayMouseMove}
      onMouseUp={handleOverlayMouseUp}
      onClick={(e) => e.stopPropagation()}
    >
      <div 
        className="modal" 
        onClick={handleModalClick} 
        onMouseDown={handleModalMouseDown}
        onMouseUp={(e) => e.stopPropagation()}
        style={{ maxWidth: 550 }}
      >
        <div className="modal-header">
          <h2>Edit Category</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
          {/* Category Name Edit */}
          <label style={{ color: '#047857', fontWeight: 600 }}>Category Name:</label>
          <input
            className="input-field"
            placeholder="Category Name"
            value={editCategoryName}
            onChange={(e) => setEditCategoryName(e.target.value)}
            data-testid="edit-category-name-input"
          />
          
          <div style={{ 
            background: 'rgba(252, 211, 77, 0.1)', 
            padding: 15, 
            borderRadius: 10,
            borderLeft: '4px solid #FCD34D'
          }}>
            <p style={{ color: '#a1a1aa', fontSize: '0.85rem' }}>
              <strong>Current Protocol:</strong>
            </p>
            <code style={{ 
              display: 'block',
              background: 'rgba(0,0,0,0.3)', 
              padding: 10, 
              borderRadius: 6,
              color: '#10b981',
              fontSize: '0.8rem',
              wordBreak: 'break-all',
              marginTop: 5
            }}>
              {editingCategory.protocol || '(no protocol set)'}
            </code>
          </div>
          
          <label style={{ color: '#047857', fontWeight: 600 }}>New Protocol:</label>
          <textarea
            className="input-field"
            placeholder="Enter new protocol (e.g., (keyphrase1 or keyphrase2) & (keyphrase3)+)"
            rows={5}
            value={editProtocol}
            onChange={(e) => setEditProtocol(e.target.value)}
            style={{ resize: 'vertical', fontFamily: 'monospace' }}
            data-testid="edit-protocol-input"
          />
          
          {/* Protocol Syntax Help */}
          <div style={{
            background: 'rgba(16, 185, 129, 0.1)',
            border: '1px solid rgba(16, 185, 129, 0.3)',
            borderRadius: 8,
            padding: 12,
            fontSize: '0.8rem'
          }}>
            <p style={{ color: '#10b981', fontWeight: 600, marginBottom: 8 }}>📝 Protocol Syntax Help:</p>
            <ul style={{ color: '#a1a1aa', margin: 0, paddingLeft: 20, lineHeight: 1.6 }}>
              <li><code style={{ color: '#047857' }}>or</code> - Separate alternatives within a group</li>
              <li><code style={{ color: '#047857' }}>&</code> <strong>or</strong> <code style={{ color: '#047857' }}>and</code> - Connect groups (both work!)</li>
              <li><code style={{ color: '#047857' }}>+</code> - Boost priority of a group</li>
              <li><code style={{ color: '#047857' }}>-</code> - Exclude matches containing these terms</li>
            </ul>
            <p style={{ color: '#22d3ee', marginTop: 10, marginBottom: 0 }}>
              ✨ <strong>Tip:</strong> "and" works the same as "&" between groups!<br/>
              <span style={{ color: '#a1a1aa' }}>Example: <code>(aviation or pilot) and (safety)</code></span>
            </p>
          </div>
          
          {/* Privacy Controls - 3 Radio Buttons */}
          <div style={{
            background: 'linear-gradient(135deg, rgba(252, 211, 77, 0.1), rgba(253, 230, 138, 0.1))',
            borderRadius: 12,
            padding: 15,
            border: '1px solid rgba(252, 211, 77, 0.3)'
          }}>
            <p style={{ color: '#047857', fontWeight: 600, marginBottom: 12, fontSize: '0.95rem' }}>
              🔐 Category Privacy
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {privacyOptions.map(option => (
                <label
                  key={option.value}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '12px 15px',
                    borderRadius: 10,
                    cursor: 'pointer',
                    background: (editPrivacy || editingCategory.privacy || 'private') === option.value 
                      ? `${option.color}20` 
                      : '#475569',
                    border: (editPrivacy || editingCategory.privacy || 'private') === option.value 
                      ? `2px solid ${option.color}` 
                      : '1px solid rgba(255,255,255,0.1)',
                    transition: 'all 0.2s'
                  }}
                  data-testid={`edit-privacy-${option.value}`}
                >
                  <input
                    type="radio"
                    name="edit-privacy"
                    value={option.value}
                    checked={(editPrivacy || editingCategory.privacy || 'private') === option.value}
                    onChange={(e) => setEditPrivacy(e.target.value)}
                    style={{ 
                      accentColor: option.color,
                      width: 18,
                      height: 18
                    }}
                  />
                  <span style={{ fontSize: '1.3rem' }}>{option.icon}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ 
                      color: (editPrivacy || editingCategory.privacy || 'private') === option.value ? '#fff' : '#a1a1aa',
                      fontWeight: 600,
                      fontSize: '0.9rem'
                    }}>
                      {option.label}
                    </div>
                    <div style={{ 
                      color: '#71717a', 
                      fontSize: '0.75rem',
                      marginTop: 2
                    }}>
                      {option.desc}
                    </div>
                  </div>
                </label>
              ))}
            </div>
            
            {/* Friends Only Info */}
            {(editPrivacy || editingCategory.privacy) === 'friends' && (
              <div style={{
                marginTop: 12,
                background: 'rgba(245, 158, 11, 0.15)',
                borderRadius: 8,
                padding: 12,
                border: '1px solid rgba(245, 158, 11, 0.3)'
              }}>
                <p style={{ color: '#f59e0b', fontSize: '0.8rem', margin: 0 }}>
                  👥 <strong>Friends Only:</strong> Your friends can view this protocol and copy it to their own database. 
                  The copied protocol will be private to them by default.
                </p>
              </div>
            )}
          </div>
          
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button 
              className="btn btn-secondary" 
              onClick={onClose}
              style={{ flex: 1, minWidth: 100 }}
            >
              Cancel
            </button>
            <button 
              className="btn btn-primary" 
              onClick={onSave}
              style={{ flex: 1, minWidth: 100 }}
              data-testid="save-protocol-btn"
            >
              Save Protocol
            </button>
          </div>
          
          {/* Delete Category Section */}
          {onDelete && (
            <div style={{
              marginTop: 20,
              padding: 15,
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: 10
            }}>
              {/* Clean Category Button - UNLINK ONLY */}
              <div style={{ marginBottom: 15 }}>
                <h4 style={{ color: '#f59e0b', margin: '0 0 10px 0', fontSize: '0.9rem' }}>
                  🧹 Remove Results from Category
                </h4>
                <p style={{ color: '#a1a1aa', fontSize: '0.8rem', margin: '0 0 10px 0' }}>
                  Unlink all search results from this category. Results will remain in your database and other categories.
                  <strong style={{ color: '#10b981', display: 'block', marginTop: 5 }}>
                    ✅ SAFE: Results are NOT deleted, just unlinked.
                  </strong>
                </p>
                <button
                  onClick={() => {
                    if (window.confirm(`Remove all search results from "${editingCategory.name}"?\n\nThe results will be unlinked from this category but will remain in your database and other categories.\n\nThis is SAFE and can be reversed by re-running Search & Categorize.`)) {
                      onCleanCategory && onCleanCategory(editingCategory.id, 'remove');
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '10px 16px',
                    background: 'rgba(16, 185, 129, 0.2)',
                    border: '1px solid rgba(16, 185, 129, 0.5)',
                    borderRadius: 8,
                    color: '#10b981',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  data-testid="clean-category-btn"
                >
                  🧹 Unlink Results (Safe)
                </button>
              </div>
              
              <div style={{ borderTop: '1px solid rgba(239, 68, 68, 0.3)', paddingTop: 15 }}>
                <h4 style={{ color: '#ef4444', margin: '0 0 10px 0', fontSize: '0.9rem' }}>
                  🗑️ Delete Category
                </h4>
                <p style={{ color: '#a1a1aa', fontSize: '0.8rem', margin: '0 0 10px 0' }}>
                  Permanently delete this category and all its sub-categories. Search results will remain in your database.
                  {editingCategory.subcategory_count > 0 && (
                    <span style={{ display: 'block', color: '#047857', marginTop: 5 }}>
                      ⚠️ Warning: This will also delete {editingCategory.subcategory_count} sub-categor{editingCategory.subcategory_count === 1 ? 'y' : 'ies'}!
                    </span>
                  )}
                </p>
                <button
                  onClick={() => {
                    if (window.confirm(`DELETE "${editingCategory.name}" and all its sub-categories?\n\n⚠️ This CANNOT be undone!\n\nNote: Search results will remain in your database.`)) {
                      onDelete(editingCategory.id);
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '10px 16px',
                    background: 'rgba(239, 68, 68, 0.2)',
                    border: '1px solid rgba(239, 68, 68, 0.5)',
                    borderRadius: 8,
                    color: '#ef4444',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  data-testid="delete-category-btn"
                >
                  🗑️ Delete Category
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export { CreateCategoryModal, EditCategoryModal };
